import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { filterBoolean, provide } from '@core/common-legacy';
import {
  AdminProfilePasswordFormComponent,
  AuthenticationDialogComponent,
  PasswordChangeService,
  provideAuthTranslations,
} from '@core/components-authorization-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  AppActivationDialogComponent,
  SmsActivationDialogComponent,
} from '@core/security-smart-ui';
import {
  TellentGrantAccessActions,
  TellentGrantAccessSelectors,
  TellentTwoFactorAuthenticationActions,
  TellentTwoFactorAuthenticationSelectors,
  // eslint-disable-next-line boundaries/element-types -- FIXME
} from '@core/tellent-data'; // FIXME
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { SignInConsentStatus } from '@core/tellent-types';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  DialogService,
  DialogWidth,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  PlaceloaderComponent,
  ProviderItemComponent,
  RtPlatform,
} from '@core/user-interface-legacy';

import { tellentAuthModuleTranslations } from '../../services/tellent-admin-authentication-dialog.service';
import { TellentAdminPasswordChangeService } from '../../services/tellent-admin-password-change.service';
import { CurrentUserAnalyticsService } from '../current-user.analytics';
import { ApprovalHistoryTableComponent } from './approval-history-table/approval-history-table.component';
import { CurrentUserProfileSecurityDialogService } from './current-user-profile-security-dialog.service';

export enum Providers {
  App = 'totp',
  Sms = 'sms',
}

const paginationDefault = {
  payload: {
    scope: 'main',
    params: { page: 1, limit: 5 },
  },
};

@Component({
  selector: 'tlc-current-user-profile-security',
  templateUrl: './current-user-profile-security.component.html',
  styleUrls: ['./current-user-profile-security.component.less'],
  imports: [
    TranslatePipe,
    ButtonComponent,
    DropdownItemComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    IconComponent,
    IconButtonComponent,
    ProviderItemComponent,
    HeadingComponent,
    OverlayOriginDirective,
    NgTemplateOutlet,
    InfoboxComponent,
    DividerComponent,
    ApprovalHistoryTableComponent,
    PlaceloaderComponent,
  ],
})
export class CurrentUserProfileSecurityComponent {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly store = inject(Store);
  private readonly dialogSecurityService = inject(CurrentUserProfileSecurityDialogService);
  private readonly analytics = inject(CurrentUserAnalyticsService);
  private readonly actions = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platform = inject(RtPlatform);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public lastRecovery: string;
  public providers: typeof Providers = Providers;
  public DropdownWidth = DropdownWidth;

  public accessData = [];

  public data = this.store.selectSignal(TellentTwoFactorAuthenticationSelectors.data);

  public isOrgOwner = this.currentUserFacade.isOrgOwner;

  public signInConsentPaginatedList = this.store.selectSignal(
    TellentGrantAccessSelectors.entitiesWithPagination({ scope: 'main' }),
  );
  public signInConsentList = this.store.selectSignal(TellentGrantAccessSelectors.allEntities);

  public currentMembership = this.currentUserFacade.organizationMembership;

  public pagination = computed(() => this.signInConsentPaginatedList().meta);

  public rows = computed(() => this.signInConsentPaginatedList().rows.sort((a, b) => b.id - a.id));
  public isAccessGranted = computed(() =>
    this.signInConsentList().find(row => row.status === SignInConsentStatus.Active),
  );

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    this.store.dispatch(TellentTwoFactorAuthenticationActions.fetch());
    if (this.currentMembership())
      this.store.dispatch(TellentGrantAccessActions.fetchPaginatedCollection(paginationDefault));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get canEnable2fa() {
    return this.data()?.canEnable2fa;
  }

  // waiting for account recovery
  // public dismissRecoveryInfobox() {
  //   //
  // }

  public openSms2FactorActivationDialog() {
    return this.dialogService.openCustom(
      SmsActivationDialogComponent,
      {
        width: DialogWidth.Small,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      {},
      this.vcRef,
    );
  }

  public openApp2FactorActivationDialog() {
    return this.dialogService.openCustom(
      AppActivationDialogComponent,
      {
        width: DialogWidth.Medium,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      {},
      this.vcRef,
    );
  }

  public disable2FactorAuth(provider: Providers) {
    this.dialogService
      .openCustom(
        AuthenticationDialogComponent,
        {
          width: DialogWidth.SmallX,
        },
        {},
        this.vcRef,
      )
      .afterClosed.pipe(take(1), filterBoolean)
      .subscribe((securityToken: string) =>
        this.dialogSecurityService.showConfirmation2FaDialog(securityToken, provider),
      );
  }

  public grantAccess() {
    this.store.dispatch(TellentGrantAccessActions.grantAccess());
    this.analytics.trackGrandAccess('grant_access');

    this.actions
      .pipe(
        ofType(TellentGrantAccessActions.grantAccessSuccess),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.onPageChanged(1);
      });
  }

  public revokeAccess() {
    this.store.dispatch(TellentGrantAccessActions.revokeAccess());
    this.analytics.trackGrandAccess('revoke_access');
  }

  public async deleteAccount() {
    if (this.isOrgOwner()) {
      this.dialogSecurityService.openIsOwnerDialog();
      return;
    }

    const { afterClosed } = this.dialogService.openCustom(
      AuthenticationDialogComponent,
      {
        width: DialogWidth.SmallX,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      {},
      this.vcRef,
    );
    const emailCode = await firstValueFrom(afterClosed);

    if (emailCode) {
      this.dialogSecurityService.openConfirmDeleteDialog(emailCode);
    }
  }

  public async resetPassword() {
    const { afterClosed } = this.dialogService.openCustom(
      AuthenticationDialogComponent,
      {
        width: DialogWidth.SmallX,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      {},
      this.vcRef,
    );
    const emailCode = await firstValueFrom(afterClosed);

    if (emailCode)
      this.dialogService.custom(
        AdminProfilePasswordFormComponent,
        {
          width: DialogWidth.SmallX,
          providers: [
            provide(PasswordChangeService, { useExisting: TellentAdminPasswordChangeService }),
            provideAuthTranslations({ useValue: tellentAuthModuleTranslations }),
          ],
        },
        { secureChangeToken: emailCode },
      );
  }

  public onPageChanged(page: number) {
    const params = {
      limit: paginationDefault.payload.params.limit,
      page,
    };
    this.store.dispatch(
      TellentGrantAccessActions.fetchPaginatedCollection({ payload: { scope: 'main', params } }),
    );
  }
}
